/* global bassConfig, COMSCORE, dfpAdUnits, ga, AdLayersAPI */

import typeConfig from './config/type-config';
import typeMap from './config/type-map';
import {
  sliceSize, sliceMap, slotSize, slotMap,
} from './config/size-map';

// NOTE: Important that we wait on window load since we're manipulating a React element.
// as the js loaded here works with on-page react apps.
window.addEventListener('load', () => {
  // Destructure the window object.
  const {
    dfpAdDetails,
    googletag,
    history,
    location: {
      search = '',
    } = {},
  } = window;

  // Bail if our config hasn't loaded.
  if (typeof bassConfig === 'undefined') {
    return;
  }

  let leaderboardCount = 0;
  let pos = '';

  // Destructure our bass
  const {
    bass_leaderboard: { page = '', refresh_ads: refresh = false, ad_refresh_interval: adRefreshInterval } = {},
    // So default here for now.
    bass_aoy: { current: currentView = '', year = '2022' } = {},
  } = bassConfig || {};

  /**
   * Track url and get last element of the url.
   * We'll assume these will exist on the basstrakk subpage.
   */
  const urlParts = window.location.pathname.split('/')
    .filter((part) => ![''].includes(part));
  const useSlug = urlParts[urlParts.length - 1];

  // Setup Object accessor.
  const config = {
    year: year || new Date().getFullYear(),
    type: typeMap.get(currentView),
    current: currentView,
    // Setters.
    set setCurrent(next) {
      if (next !== this.current) {
        this.currentListener();
      }
      this.current = next;
    },
    set setType(next) {
      if (next !== this.type) {
        this.currentListener();
      }
      this.type = next;
    },
    set setYear(next) {
      if (next !== this.year) {
        this.currentListener();
      }
      this.year = next;
    },
    // Getters.
    get getConfig() {
      return typeConfig[page][this.current];
    },
    get getTarget() {
      const format = page === 'basstrakk' ? useSlug : typeConfig[page][this.current].format;

      // Bail if no format exists.
      if (!dfpAdDetails || !format || !dfpAdDetails[format]) {
        return null;
      }

      return dfpAdDetails[format];
    },
    // Listeners.
    currentListener() {},
    registerNewListener(newListener) {
      this.currentListener = newListener;
    },
  };

  /**
   * Handle refresh on window resize.
   */
  const refreshAds = () => {
    // Trigger function only when done resizing.
    clearTimeout(window.refreshFinished);
    window.refreshFinished = setTimeout(() => {
      googletag.pubads().refresh();
    }, 250);
  };

  /**
   * Update history state and url.
   */
  const updateURL = () => {
    // Construct URLSearchParams object instance from current URL querystring.
    const queryParams = new URLSearchParams(search);
    // Set new or modify existing parameter value.
    queryParams.set('view', typeMap.get(config.current));
    // Replace current querystring with the new one.
    history.replaceState(null, null, `?${queryParams.toString()}`);
    /**
     * Refreshing here to avoid hitting Google Ads single request architecture limits.
     * See BASS-1272.
     * Only triggers on aoy `/standings` page.
     */
    if (bassConfig && bassConfig.bass_aoy && bassConfig.bass_aoy.id) {
      window.location.reload();
    }
  };

  /**
   * Update Title for the Page.
   */
  const setTitle = () => {
    const entryTitle = document.querySelector('h1.entry-title');

    if (!entryTitle) {
      return;
    }

    const currentConfig = config.getConfig;

    if (currentConfig && currentConfig.title) {
      entryTitle.innerText = currentConfig.title;
    }
  };

  /**
   * Setup click handler on tabs.
   */
  const setupTabHandler = () => {
    // These are our app tabs.
    const tableTabs = document.querySelectorAll('.react-tabs__tab');

    if (!tableTabs) {
      return;
    }

    // NOTE: This adds a click listener to the AOY/Basstrakk tabs.
    // Add event listeners to tableTabs.
    tableTabs.forEach((element) => {
      element.addEventListener('click', (event) => {
        const tab = event.target;
        // Get the values of the selected tab. Assuming we're changing tabs.
        // Find drupal-tab class.
        const foundClass = [...tab.classList].find(((string) => string.startsWith('drupal-tab-'))) || null;
        // If we get a match, get type name.
        if (foundClass) {
          const typeName = foundClass.split('-').pop();
          config.setCurrent = typeName;
          updateURL();
          googletag.cmd.push(() => {
            googletag.pubads().setTargeting('tab', typeName);
          });
          refreshAds();
        }
      });
    });
  };

  /**
   * Setup click handler on the year dropdown.
   */
  const setupYearHandler = () => {
    // This is our app year dropdown.
    const yearSelectContainer = document.querySelector('.aoy-year-selector');

    if (!yearSelectContainer) {
      return;
    }

    // Get select element.
    const yearSelect = yearSelectContainer.querySelector('.aoy-standings-year');

    // Add value to the config object.
    config.setYear = yearSelect.value;

    yearSelect.addEventListener('change', (event) => {
      const dropdown = event.target;
      if (dropdown.value) {
        config.setYear = dropdown.value;
      }
    });
  };

  /**
   * Setup click handler on the type dropdown.
   */
  const setupTypeHandler = (init = true) => {
    // This is our app year dropdown.
    const typeSelectContainer = document.querySelector('.aoy-type-selector');

    if (!typeSelectContainer) {
      return;
    }

    // Get select element.
    const typeSelect = typeSelectContainer.querySelector('.aoy-standings-type');

    // Add value to the config object.
    config.setType = typeSelect.value;

    if (init) {
      typeSelect.addEventListener('change', (event) => {
        const dropdown = event.target;
        if (dropdown.value) {
          config.setType = dropdown.value;
        }
      });
    }
  };

  /**
   * Create a new slot/slice for each view.
   */
  const setSlices = () => {
    // If we don't have add support hooked up, do nothing.
    if (!config.getTarget) {
      return;
    }

    // NOTE: This is for interstitials.
    // This is the ad interstitials.
    const slices = document.querySelectorAll('.advertisement');

    if (!slices) {
      return;
    }

    // Only if we have our targets.
    // Add all table rows into the target for our interstitial.
    slices.forEach((element) => {
      const adSlot = element.querySelector('[id^="dfp-ad-"]');

      if (!adSlot) {
        return;
      }

      const slotName = adSlot.getAttribute('id');
      if (!config.getTarget.targeting[slotName]) {
        config.getTarget.targeting.push(slotName);
      }
    });

    config.getTarget.targeting.forEach((slotName) => {
      if (!dfpAdUnits[slotName] && slotName && typeof AdLayersAPI !== 'undefined') {
        const format = config.getConfig.format === 'basstrakk' ? useSlug : config.getConfig.format;
        if (
          (slotName.includes('cutline-'))
          && (slotName.split('cutline-').length > 1)
          && (!Number.isNaN(parseInt(slotName.split('cutline-')[1], 10)))
        ) {
          const value = parseInt(slotName.split('cutline-')[1], 10);
          pos = `cutline${value}`;
        } else if (format === 'leaderboard' || config.getConfig.format === 'basstrakk') {
          leaderboardCount += 1;
          pos = leaderboardCount === 1 ? 'mid' : `mid${leaderboardCount}`;
        } else {
          pos = '';
        }
        (new AdLayersAPI()).lazyLoadAd({
          slotName,
          format,
          sizes: sliceSize,
          sizeMapping: sliceMap,
          targeting: { pos },
        });
      }
    });
  };

  /**
   * Add event listeners for each table row.
   */
  const setTableRows = () => {
    // If we don't have add support hooked up, do nothing.
    if (!config.getTarget) {
      return;
    }

    // This class/element exists for all AOY and BT apps.
    const tableRows = document.querySelectorAll('.bass-table__row');

    if (!tableRows) {
      return;
    }

    // NOTE: This is for click handling in AOY/BASS apps.
    tableRows.forEach((element) => element.addEventListener(
      'click',
      () => {
        setTimeout(() => {
          const container = element.nextSibling;
          const anglerExpanded = container.querySelector('.angler-expanded');
          const anglerName = anglerExpanded?.parentElement?.previousElementSibling?.querySelector('.angler-name')?.dataset?.angler ?? '';
          const adContainer = container.querySelector('.dfp-tag-wrapper');

          if (!adContainer) {
            return;
          }

          adContainer.setAttribute('id', `dfp-ad-${config.getConfig.slot}-${typeMap.get(config.current)}-wrapper-${anglerExpanded.getAttribute('itemid')}`);
          const slotName = adContainer.getAttribute('id');

          if (!config.getTarget.targeting[slotName]) {
            config.getTarget.targeting.push(slotName);
          }

          if (!dfpAdUnits[slotName] && typeof AdLayersAPI !== 'undefined') {
            const format = config.getConfig.format === 'basstrakk' ? useSlug : config.getConfig.format;
            (new AdLayersAPI()).lazyLoadAd({
              slotName,
              format,
              sizes: slotSize,
              sizeMapping: slotMap,
              targeting: {
                angler: anglerName.toLowerCase().replaceAll(' ', '-'),
                pos: 'basstrakk',
              },
            });
          }

          // Only refresh ad slot for angler in row that was expanded.
          googletag.pubads().refresh([slotName]);
        }, 750);
      },
    ));
  };

  // Kickoff on load.
  setupTabHandler();
  setupTypeHandler();
  setupYearHandler();
  setSlices();
  setTableRows();
  setTitle();

  // Handle all of the changes for ads on tab change.
  config.registerNewListener(() => {
    setTimeout(() => {
      setupTabHandler();
      setupTypeHandler(false);
      setupYearHandler();
      setSlices();
      setTableRows();
      setTitle();
    }, 1250);
  });

  /**
   * Trigger analytics.
   */
  const triggerAnalytics = () => {
    // Trigger Page View for Omniture, Google Analytics, and Comscore
    // Google.
    ga('send', 'pageview');

    // Comscore.
    COMSCORE.beacon({
      c1: '2',
      c2: '11154750',
      c4: window.location.href,
    });
  };

  /**
   * Trigger ads.
   */
  const triggerAds = () => {
    // Refresh ads only on certain pages.
    if (refresh) {
      googletag.cmd.push(() => {
        googletag.pubads().refresh();
      });
    }
  };

  /**
   * Trigger analytics at a regular interval
   */
  setInterval(() => {
    triggerAnalytics();
  }, 180000);

  /**
   * Trigger ad refresh at a regular interval
   */
  if (adRefreshInterval) {
    setInterval(() => {
      triggerAds();
    }, adRefreshInterval);
  }
});
